import React from 'react'
import { Logger } from 'wdc-cube'
import { bindUpdate } from 'wdc-cube-react'

import { styled } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'

import AddIcon from '@mui/icons-material/Add'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'

import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import ListItemIcon from '@mui/material/ListItemIcon'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Switch from '@mui/material/Switch'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid'

import { FilterOption } from 'src/components/searchbox'
import { UserRow } from 'src/modules/admin/user-manager/um_scopes'
import { formatNumber } from 'src/utils/formatter-utils'
import {
    bannerSx,
    Breadcrumbs,
    buttonSx,
    chipSx,
    classToFComponent,
    EditorBody,
    EditorContextBar,
    EditorSideColumn,
    EditorView,
    EmptyIcon,
    FCClassContext,
    Field,
    FieldHelp,
    FieldLabel,
    IconActionButton,
    inputSx,
    KeyDivider,
    KeyRow,
    KeyValues,
    LifesHubChipTone,
    LifesHubConfirmDialog,
    ModuleView,
    PanelCard,
    PanelCardBody,
    SkeletonBlock,
    SwitchRow,
    switchSx
} from 'src/utils/views'

import PriceTierEditor from '../../../billing/views/bl_price_tier_editor'
import { GroupRow, UserGroupRow } from '../../gm_scopes'
import GroupManagerFormScope, { type GroupFormTab } from '../gm_form_scope'
import { CellDesc, CellIdentity, CellTitle } from 'src/utils/views/modules/admin-group-manager'

const LOG = Logger.get('GroupManagerFormView')

const EditorTop = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 24,
    '@media (max-width: 900px)': {
        flexDirection: 'column'
    }
})

const PageTitle = styled('h1')({
    margin: '0 0 6px',
    fontSize: 'clamp(26px, 2.1vw, 36px)',
    lineHeight: 1.15,
    letterSpacing: '-.025em',
    color: 'var(--ink)',
    overflowWrap: 'anywhere'
})

const EditorMeta = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 10,
    marginTop: 9,
    color: 'var(--muted)',
    '& .strong': { color: 'var(--ink)', fontWeight: 650 }
})

const EditorActions = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
    flexWrap: 'wrap',
    '@media (max-width: 720px)': {
        width: '100%',
        '& .MuiButton-root': { flexGrow: 1 }
    }
})

const Stack = styled('div')({
    display: 'grid',
    gap: 10,
    minWidth: 0
})

const CardHead = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 20,
    padding: '16px 18px 12px',
    flexShrink: 0,
    '& h3': { margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--ink)', lineHeight: 1.2 },
    '& .subtitle': { margin: '5px 0 0', color: 'var(--muted)', fontSize: 12.5 }
})

const SettingsGrid = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: 12,
    '@media (max-width: 1050px)': {
        gridTemplateColumns: 'minmax(0, 1fr)'
    }
})

const SettingsPanel = styled('div')({
    padding: 16,
    border: '1px solid var(--border)',
    borderRadius: 11,
    backgroundColor: '#fbfcfb',
    minWidth: 0,
    '& .MuiOutlinedInput-root': {
        minHeight: 40,
        borderRadius: 9,
        backgroundColor: '#fff',
        fontSize: 13.5,
        '& fieldset': { borderColor: 'var(--border-strong)' },
        '&.Mui-focused fieldset': { borderColor: 'var(--green-700)', borderWidth: 1 },
        '&.Mui-focused': { boxShadow: 'rgba(38,229,31,.12) 0 0 0 3px' }
    },
    '& .MuiButton-root': { textTransform: 'none', fontWeight: 650, color: 'var(--green-800)' }
})

const FilterRow = styled('div')({
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    minWidth: 0,
    '& b': {
        display: 'block',
        fontSize: 12.5,
        fontWeight: 700,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    }
})

const EmptyState = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '40px 20px',
    textAlign: 'center',
    '& h3': { margin: 0, fontSize: 16, color: 'var(--ink)' },
    '& p': { margin: 0, color: 'var(--muted)', maxWidth: 420 }
})

const useStyles = makeStyles()({
    tabs: {
        marginTop: 16,
        minHeight: 42,
        '& .MuiTabs-indicator': {
            height: 3,
            borderRadius: '3px 3px 0 0',
            backgroundColor: 'var(--green)'
        },
        '& .MuiTab-root': {
            minHeight: 42,
            padding: '10px 14px 12px',
            textTransform: 'none',
            fontSize: 14,
            fontWeight: 650,
            color: '#4e5752',
            '&:hover': { color: 'var(--green-800)' }
        },
        '& .MuiTab-root.Mui-selected': { color: 'var(--green-800)' }
    },
    grid: {
        minHeight: 320,
        border: 'none',
        fontSize: 13.5,
        color: 'var(--ink)',
        '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': { height: '0.8em' },
        '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#fbfcfb',
            borderBottom: '1px solid var(--border)',
            borderRadius: 0
        },
        '& .MuiDataGrid-columnHeaderTitle': { fontSize: 11.5, fontWeight: 700, color: '#2a302c' },
        '& .MuiDataGrid-cell': { borderBottom: '1px solid var(--border)' },
        '& .MuiDataGrid-footerContainer': { borderTop: '1px solid var(--border)', color: 'var(--muted)' },
        '& .MuiDataGrid-row:hover': { background: 'linear-gradient(90deg, var(--green-050), #fbfffb)' },
        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' }
    },
    rowMenu: {
        '& .MuiPaper-root': {
            minWidth: 216,
            padding: 7,
            borderRadius: 10,
            border: '1px solid var(--border)',
            boxShadow: 'var(--shadow-2)',
            backgroundImage: 'none'
        },
        '& .MuiMenuItem-root': {
            gap: 9,
            padding: '9px 10px',
            borderRadius: 7,
            fontSize: 13.5,
            minHeight: 0,
            '&:hover': { backgroundColor: '#f0f4f1' }
        },
        '& .MuiListItemIcon-root': {
            minWidth: 0,
            color: 'inherit',
            '& .MuiSvgIcon-root': { fontSize: 18 }
        }
    },
    dangerItem: {
        color: 'var(--danger)'
    },
    addUserRow: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        gap: 12,
        alignItems: 'end',
        '@media (max-width: 720px)': {
            gridTemplateColumns: 'minmax(0, 1fr)',
            '& .MuiButton-root': { width: '100%' }
        }
    }
})

const TABS: { key: GroupFormTab; label: string }[] = [
    { key: 'config', label: 'Configuração' },
    { key: 'filters', label: 'Filtros de busca' },
    { key: 'billing', label: 'Faturamento' },
    { key: 'users', label: 'Usuários' }
]

type GroupManagerFormViewProps = {
    className?: string
    scope: GroupManagerFormScope
}

class GroupManagerFormViewClass implements FCClassContext<GroupManagerFormViewProps> {
    readonly emitOnSave = () => {
        this.scope.onSave().catch(LOG.caught)
    }

    readonly emitOnClose = () => {
        this.scope.onClose().catch(LOG.caught)
    }

    readonly emitTabChange = (_event: React.SyntheticEvent, tab: GroupFormTab) => {
        this.scope.onTabSelected(tab).catch(LOG.caught)
    }

    readonly emitFormDataChange = (dadosAlterados: Partial<GroupRow>) => {
        this.scope.onFormDataChange(dadosAlterados).catch(LOG.caught)
    }

    readonly emitBillingUseDefaultChanged = (useDefault: boolean) => {
        this.scope.onBillingUseDefaultChanged(useDefault).catch(LOG.caught)
    }

    readonly emitAddUserToGroup = () => {
        this.scope.onAddUserToGroup().catch(LOG.caught)
    }

    readonly emitSelectedUserChange = (_event: unknown, user: UserRow | string | null) => {
        this.scope.onSelectedUserChanged(typeof user === 'string' || !user ? undefined : user).catch(LOG.caught)
    }

    readonly emitDiscard = () => {
        this.scope.onDiscard().catch(LOG.caught)
    }

    readonly emitOpenRowMenu = (row: UserGroupRow) => (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation()
        this.rowMenuAnchor = event.currentTarget
        this.scope.onOpenRowMenu(Number(row.id)).catch(LOG.caught)
    }

    readonly emitCloseRowMenu = () => {
        this.scope.onCloseRowMenu().catch(LOG.caught)
    }

    readonly emitMenuSetAdmin = () => {
        const userId = this.scope.rowMenuUserId
        if (userId !== undefined) {
            this.scope.onSetUserToAdmin(userId).catch(LOG.caught)
        }
    }

    readonly emitMenuRemoveUser = () => {
        const userId = this.scope.rowMenuUserId
        if (userId !== undefined) {
            this.scope.onRemoveUserFromGroup(userId).catch(LOG.caught)
        }
    }

    readonly emitOnCloseModalStatusChanged = () => {
        this.scope.onCloseModalStatusChanged().catch(LOG.caught)
    }

    readonly emitOnConfirmStatusChanged = () => {
        this.scope.onConfirmStatusChanged().catch(LOG.caught)
    }

    // :: Fields

    scope!: GroupManagerFormScope
    classes!: ReturnType<typeof useStyles>['classes']
    columns = buildUserColumns(this.emitOpenRowMenu)

    rowMenuAnchor?: HTMLElement

    // :: Methods

    onSyncState({ scope }: GroupManagerFormViewProps) {
        this.scope = scope
        this.classes = useStyles().classes
        this.columns = buildUserColumns(this.emitOpenRowMenu, scope.formData?.id, scope.formData?.admin_id)
    }

    render({ className, scope }: GroupManagerFormViewProps) {
        this.scope = bindUpdate(React, scope)

        return (
            <ModuleView className={className}>
                <EditorView>
                    {this.renderEditorContext()}
                    {scope.waiting ? this.renderSkeleton() : this.renderBody()}
                </EditorView>
                {this.renderRowMenu()}
                <LifesHubConfirmDialog
                    open={scope.confirmStatusChangedModalOpen}
                    title="Salvar alteração de status"
                    subtitle={scope.formData?.name}
                    tone="warning"
                    message={
                        scope.formData?.active
                            ? 'Ao ativar o grupo, todos os usuários prioritários vinculados a ele voltam a ter acesso ao sistema.'
                            : 'Ao inativar o grupo, todos os usuários prioritários vinculados a ele perdem o acesso ao sistema.'
                    }
                    confirmLabel="Salvar e aplicar"
                    confirmIcon={<SaveOutlinedIcon />}
                    footerNote="Nada é gravado enquanto você não confirmar."
                    onCancel={this.emitOnCloseModalStatusChanged}
                    onConfirm={this.emitOnConfirmStatusChanged}
                />
            </ModuleView>
        )
    }

    renderEditorContext() {
        const { classes, scope } = this
        const dirty = scope.dirty

        const isNew = !scope.formData?.id
        const name = scope.formData?.name?.trim()
        const status = statusOf(scope.formData)

        return (
            <EditorContextBar>
                <EditorTop>
                    <div>
                        <Breadcrumbs>
                            Administração / Grupos / <b>{isNew ? 'Novo grupo' : name || 'Grupo sem nome'}</b>
                        </Breadcrumbs>
                        <PageTitle>{isNew ? 'Novo grupo' : name || 'Grupo sem nome'}</PageTitle>
                        <EditorMeta>
                            <Chip size="small" label={status.label} sx={chipSx(status.tone)} />
                            {!isNew && <span className="strong">ID {scope.formData.id}</span>}
                            {dirty && <Chip size="small" label="Alterações não salvas" sx={chipSx('orange')} />}
                            {isNew && <span>Ainda não salvo</span>}
                        </EditorMeta>
                    </div>
                    <EditorActions>
                        <Button sx={buttonSx()} onClick={this.emitOnClose} startIcon={<ArrowBackIcon />}>
                            Voltar
                        </Button>
                        <Button sx={buttonSx()} onClick={this.emitDiscard} disabled={!dirty}>
                            Descartar alterações
                        </Button>
                        <Button sx={buttonSx('primary')} onClick={this.emitOnSave} startIcon={<SaveOutlinedIcon />}>
                            Salvar grupo
                        </Button>
                    </EditorActions>
                </EditorTop>
                <Tabs
                    className={classes.tabs}
                    value={scope.activeTab}
                    onChange={this.emitTabChange}
                    variant="scrollable"
                >
                    {TABS.map((tab) => (
                        <Tab key={tab.key} value={tab.key} label={tab.label} />
                    ))}
                </Tabs>
            </EditorContextBar>
        )
    }

    renderSkeleton() {
        return (
            <EditorBody>
                <Stack>
                    <SkeletonBlock sx={{ height: 260 }} />
                    <SkeletonBlock sx={{ height: 180 }} />
                </Stack>
                <EditorSideColumn>
                    <SkeletonBlock sx={{ height: 300 }} />
                </EditorSideColumn>
            </EditorBody>
        )
    }

    renderBody() {
        return (
            <EditorBody>
                <Stack>{this.renderTab()}</Stack>
                <EditorSideColumn>{this.renderSummary()}</EditorSideColumn>
            </EditorBody>
        )
    }

    renderTab() {
        switch (this.scope.activeTab) {
            case 'filters':
                return this.renderFilters()
            case 'billing':
                return this.renderBilling()
            case 'users':
                return this.renderUsers()
            default:
                return this.renderConfig()
        }
    }

    renderConfig() {
        const { scope } = this

        const isNew = !scope.formData?.id
        const unlimited = !!scope.formData?.unlimited_data_export
        const missingName = scope.dirty && !scope.formData?.name?.trim()

        return (
            <>
                {isNew && (
                    <Alert variant="outlined" icon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />} sx={bannerSx('blue')}>
                        O grupo nasce inativo. Configure os filtros, a franquia de exportação e os usuários antes de
                        ativá-lo.
                    </Alert>
                )}

                <PanelCard elevation={0}>
                    <CardHead>
                        <div>
                            <h3>Identificação</h3>
                            <p className="subtitle">Como o grupo aparece na listagem e nos relatórios.</p>
                        </div>
                    </CardHead>
                    <PanelCardBody key={`identity-${scope.formVersion}`}>
                        <Field>
                            <FieldLabel htmlFor="gmGroupName">Nome do grupo</FieldLabel>
                            <TextField
                                id="gmGroupName"
                                size="small"
                                defaultValue={scope.formData?.name || ''}
                                onChange={(event) => this.emitFormDataChange({ name: event.target.value })}
                                placeholder="Nome que identifica o cliente ou a equipe"
                                error={missingName}
                                helperText={
                                    missingName
                                        ? 'O nome é obrigatório — sem ele o grupo não pode ser salvo.'
                                        : 'Use o nome pelo qual o cliente é reconhecido internamente.'
                                }
                                sx={inputSx}
                                fullWidth
                            />
                        </Field>
                    </PanelCardBody>
                </PanelCard>

                <PanelCard elevation={0}>
                    <CardHead>
                        <div>
                            <h3>Comportamento do grupo</h3>
                            <p className="subtitle">O que os usuários vinculados podem fazer no sistema.</p>
                        </div>
                    </CardHead>
                    <PanelCardBody>
                        <SwitchRow>
                            <Switch
                                size="small"
                                checked={!!scope.formData?.active}
                                onChange={(event) => this.emitFormDataChange({ active: event.target.checked })}
                                sx={switchSx}
                                slotProps={{
                                    input: { 'aria-label': 'Grupo ativo' }
                                }}
                            />
                            <div>
                                <b>Grupo ativo</b>
                                <FieldHelp>
                                    Ao desligar, os usuários prioritários do grupo perdem o acesso ao sistema. A troca
                                    de status é confirmada antes de gravar.
                                </FieldHelp>
                            </div>
                        </SwitchRow>

                        <SwitchRow>
                            <Switch
                                size="small"
                                checked={!!scope.formData?.crm_enrichment_enabled}
                                onChange={(event) =>
                                    this.emitFormDataChange({ crm_enrichment_enabled: event.target.checked })
                                }
                                sx={switchSx}
                                slotProps={{
                                    input: { 'aria-label': 'Enriquecimento CRM externo' }
                                }}
                            />
                            <div>
                                <b>Enriquecimento CRM externo</b>
                                <FieldHelp>
                                    Libera o envio de empresas para o CRM integrado do cliente e o retorno dos dados
                                    enriquecidos.
                                </FieldHelp>
                            </div>
                        </SwitchRow>
                    </PanelCardBody>
                </PanelCard>

                <PanelCard elevation={0}>
                    <CardHead>
                        <div>
                            <h3>Franquia de exportação</h3>
                            <p className="subtitle">Quantos registros o grupo pode exportar por competência.</p>
                        </div>
                    </CardHead>
                    <PanelCardBody>
                        <SwitchRow>
                            <Switch
                                size="small"
                                checked={unlimited}
                                onChange={(event) =>
                                    this.emitFormDataChange({ unlimited_data_export: event.target.checked })
                                }
                                sx={switchSx}
                                slotProps={{
                                    input: { 'aria-label': 'Exportação ilimitada' }
                                }}
                            />
                            <div>
                                <b>Exportação ilimitada</b>
                                <FieldHelp>
                                    Sem franquia, nada é apurado como excedente e o grupo não gera cobrança de
                                    exportação.
                                </FieldHelp>
                            </div>
                        </SwitchRow>

                        {!unlimited && (
                            <Field key={`limit-${scope.formVersion}`}>
                                <FieldLabel htmlFor="gmExportLimit">Limite de registros por competência</FieldLabel>
                                <TextField
                                    id="gmExportLimit"
                                    size="small"
                                    type="number"
                                    defaultValue={scope.formData?.limit_export_quantity ?? ''}
                                    onChange={(event) =>
                                        this.emitFormDataChange({
                                            limit_export_quantity: Number(event.target.value)
                                        })
                                    }
                                    helperText="O que passar deste total no mês é apurado como excedente no faturamento."
                                    sx={inputSx}
                                    fullWidth
                                    slotProps={{
                                        htmlInput: { min: 0, step: 1 }
                                    }}
                                />
                            </Field>
                        )}
                    </PanelCardBody>
                </PanelCard>
            </>
        )
    }

    renderFilters() {
        const { scope } = this

        return (
            <>
                <Alert variant="outlined" icon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />} sx={bannerSx('blue')}>
                    Os filtros marcados ficam disponíveis na busca dos usuários deste grupo. Os marcados como compliance
                    só aparecem para quem estiver em modo compliance.
                </Alert>

                {this.renderFilterCard(
                    'Relações societárias',
                    'Filtros oferecidos na busca do módulo de relações societárias.',
                    scope.tc_filter_menu_options_array,
                    scope.formData?.tc_sb_active_filters,
                    (filters) => this.emitFormDataChange({ tc_sb_active_filters: filters })
                )}

                {this.renderFilterCard(
                    'Profissionais médicos',
                    'Filtros oferecidos na busca do módulo de profissionais médicos.',
                    scope.ta_filter_menu_options_array,
                    scope.formData?.ta_sb_active_filters,
                    (filters) => this.emitFormDataChange({ ta_sb_active_filters: filters })
                )}

                {this.renderFilterCard(
                    'The Map',
                    'Filtros oferecidos na busca do módulo de mapa.',
                    scope.tm_filter_menu_options_array,
                    scope.formData?.tcm_sb_active_filters,
                    (filters) => this.emitFormDataChange({ tcm_sb_active_filters: filters })
                )}
            </>
        )
    }

    renderFilterCard(
        title: string,
        subtitle: string,
        options: FilterOption[],
        active: Record<string, boolean> | undefined,
        onChange: (filters: Record<string, boolean>) => void
    ) {
        const selected = options.filter((option) => Boolean(active?.[option.key])).length

        return (
            <PanelCard elevation={0}>
                <CardHead>
                    <div>
                        <h3>{title}</h3>
                        <p className="subtitle">{subtitle}</p>
                    </div>
                    <Chip size="small" label={`${selected} de ${options.length}`} sx={chipSx('gray')} />
                </CardHead>
                <PanelCardBody>
                    {options.length === 0 ? (
                        <EmptyState>
                            <EmptyIcon>
                                <InfoOutlinedIcon />
                            </EmptyIcon>
                            <h3>Nenhum filtro disponível</h3>
                            <p>A lista de filtros deste módulo ainda não foi carregada.</p>
                        </EmptyState>
                    ) : (
                        <SettingsGrid>
                            {options.map((option) => (
                                <FilterRow key={option.key}>
                                    <Switch
                                        size="small"
                                        checked={Boolean(active?.[option.key])}
                                        onChange={(event) =>
                                            onChange({ ...active, [option.key]: event.target.checked })
                                        }
                                        sx={switchSx}
                                        slotProps={{
                                            input: { 'aria-label': option.label }
                                        }}
                                    />
                                    <div style={{ minWidth: 0 }}>
                                        <b title={option.label}>{option.label}</b>
                                        {option.compliance && (
                                            <Chip
                                                size="small"
                                                label="Compliance"
                                                sx={{ ...chipSx('gray'), height: 20, marginTop: '3px' }}
                                            />
                                        )}
                                    </div>
                                </FilterRow>
                            ))}
                        </SettingsGrid>
                    )}
                </PanelCardBody>
            </PanelCard>
        )
    }

    renderBilling() {
        const { scope } = this

        return (
            <PanelCard elevation={0}>
                <CardHead>
                    <div>
                        <h3>Cobrança de excedente</h3>
                        <p className="subtitle">Preço por registro exportado acima da franquia do grupo.</p>
                    </div>
                    <Chip
                        size="small"
                        label={scope.billingUseDefault ? 'Contrato padrão' : 'Tabela própria'}
                        sx={chipSx(scope.billingUseDefault ? 'gray' : 'blue')}
                    />
                </CardHead>
                <PanelCardBody>
                    <SwitchRow>
                        <Switch
                            size="small"
                            checked={scope.billingUseDefault}
                            onChange={(event) => this.emitBillingUseDefaultChanged(event.target.checked)}
                            sx={switchSx}
                            slotProps={{
                                input: { 'aria-label': 'Usar a tabela de preços do contrato padrão' }
                            }}
                        />
                        <div>
                            <b>Usar a tabela do contrato padrão</b>
                            <FieldHelp>
                                Ao desligar, o grupo passa a ter faixas próprias e deixa de acompanhar as mudanças do
                                contrato padrão.
                            </FieldHelp>
                        </div>
                    </SwitchRow>

                    <SettingsPanel>
                        {scope.billingUseDefault ? (
                            <PriceTierEditor key="billing-default-tiers" scope={scope.billingDefaultTierEditor} />
                        ) : (
                            <PriceTierEditor key="billing-own-tiers" scope={scope.billingTierEditor} />
                        )}
                    </SettingsPanel>

                    <FieldHelp>
                        A tabela do grupo passa a valer a partir da competência corrente. Competências já apuradas
                        continuam usando a tabela que estava em vigor na época.
                    </FieldHelp>
                </PanelCardBody>
            </PanelCard>
        )
    }

    renderUsers() {
        const { classes, scope } = this

        const users = scope.formData?.users ?? []

        return (
            <PanelCard elevation={0}>
                <CardHead>
                    <div>
                        <h3>Usuários do grupo</h3>
                        <p className="subtitle">
                            {pluralize(users.length, 'usuário vinculado', 'usuários vinculados')} — as mudanças só valem
                            depois de salvar o grupo.
                        </p>
                    </div>
                </CardHead>
                <PanelCardBody>
                    <div className={classes.addUserRow}>
                        <Field>
                            <FieldLabel htmlFor="gmUserAdd">Adicionar usuário</FieldLabel>
                            <Autocomplete
                                id="gmUserAdd"
                                freeSolo
                                selectOnFocus
                                clearOnBlur
                                handleHomeEndKeys
                                options={scope.allUsers ?? []}
                                value={scope.selectedUser ? scope.selectedUser.name : ''}
                                getOptionLabel={(option) =>
                                    typeof option === 'string'
                                        ? option
                                        : option.full_name
                                          ? `${option.name} (${option.full_name})`
                                          : option.name
                                }
                                isOptionEqualToValue={(option, value) =>
                                    typeof value !== 'string' && value.id === option.id
                                }
                                onChange={this.emitSelectedUserChange}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        placeholder="Buscar por e-mail ou nome do usuário"
                                        sx={inputSx}
                                    />
                                )}
                            />
                        </Field>
                        <Button sx={buttonSx()} onClick={this.emitAddUserToGroup} startIcon={<AddIcon />}>
                            Adicionar ao grupo
                        </Button>
                    </div>

                    {users.length === 0 ? (
                        <EmptyState>
                            <EmptyIcon>
                                <InfoOutlinedIcon />
                            </EmptyIcon>
                            <h3>Nenhum usuário no grupo</h3>
                            <p>
                                Busque um usuário no campo acima para vinculá-lo. O primeiro vinculado costuma ser o
                                administrador do grupo.
                            </p>
                        </EmptyState>
                    ) : (
                        <DataGrid
                            className={classes.grid}
                            rows={users}
                            columns={this.columns}
                            getRowId={userRowId}
                            getRowHeight={userRowHeight}
                            disableRowSelectionOnClick
                            disableColumnMenu
                            initialState={{ pagination: { paginationModel: { pageSize: 15 } } }}
                            pageSizeOptions={[15, 25, 50]}
                        />
                    )}
                </PanelCardBody>
            </PanelCard>
        )
    }

    renderSummary() {
        const { scope } = this

        const form = scope.formData
        const status = statusOf(form)
        const admin = form?.admin_fullname || form?.admin_name
        const users = form?.users ?? []

        const franquia = form?.unlimited_data_export
            ? 'Ilimitada'
            : form?.limit_export_quantity
              ? `${formatNumber(form.limit_export_quantity)} registros`
              : 'Não definida'

        const tcTotal = scope.tc_filter_menu_options_array.length
        const taTotal = scope.ta_filter_menu_options_array.length
        const tmTotal = scope.tm_filter_menu_options_array.length
        const tcOn = countActive(scope.tc_filter_menu_options_array, form?.tc_sb_active_filters)
        const taOn = countActive(scope.ta_filter_menu_options_array, form?.ta_sb_active_filters)
        const tmOn = countActive(scope.tm_filter_menu_options_array, form?.tcm_sb_active_filters)

        return (
            <PanelCard elevation={0}>
                <CardHead>
                    <div>
                        <h3>Resumo do grupo</h3>
                        <p className="subtitle">O que será gravado ao salvar.</p>
                    </div>
                </CardHead>
                <PanelCardBody>
                    <KeyValues>
                        <KeyRow>
                            <span className="key">Status</span>
                            <span className="value">
                                <Chip size="small" label={status.label} sx={chipSx(status.tone)} />
                            </span>
                        </KeyRow>
                        <KeyRow>
                            <span className="key">Administrador</span>
                            <span className="value">{admin || 'Não definido'}</span>
                        </KeyRow>
                        <KeyRow>
                            <span className="key">Franquia</span>
                            <span className="value">{franquia}</span>
                        </KeyRow>
                        <KeyRow>
                            <span className="key">Tabela de preço</span>
                            <span className="value">
                                {scope.billingUseDefault
                                    ? 'Contrato padrão'
                                    : pluralize(
                                          scope.billingTierEditor.tiers.length,
                                          'faixa própria',
                                          'faixas próprias'
                                      )}
                            </span>
                        </KeyRow>
                        <KeyRow>
                            <span className="key">Enriquecimento CRM</span>
                            <span className="value">{form?.crm_enrichment_enabled ? 'Liberado' : 'Bloqueado'}</span>
                        </KeyRow>

                        <KeyDivider />

                        <KeyRow>
                            <span className="key">Usuários</span>
                            <span className="value">{formatNumber(users.length)}</span>
                        </KeyRow>
                        <KeyRow>
                            <span className="key">Filtros societários</span>
                            <span className="value">
                                {tcOn} de {tcTotal}
                            </span>
                        </KeyRow>
                        <KeyRow>
                            <span className="key">Filtros médicos</span>
                            <span className="value">
                                {taOn} de {taTotal}
                            </span>
                        </KeyRow>
                        <KeyRow>
                            <span className="key">Filtros do mapa</span>
                            <span className="value">
                                {tmOn} de {tmTotal}
                            </span>
                        </KeyRow>
                    </KeyValues>
                </PanelCardBody>
            </PanelCard>
        )
    }

    renderRowMenu() {
        const { classes, scope } = this

        const userId = scope.rowMenuUserId
        const isAdmin = userId !== undefined && userId === Number(scope.formData?.admin_id)

        return (
            <Menu
                className={classes.rowMenu}
                anchorEl={this.rowMenuAnchor}
                open={userId !== undefined}
                onClose={this.emitCloseRowMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem onClick={this.emitMenuSetAdmin} disabled={isAdmin}>
                    <ListItemIcon>
                        <MilitaryTechIcon />
                    </ListItemIcon>
                    {isAdmin ? 'Já é administrador' : 'Tornar administrador'}
                </MenuItem>
                <Divider sx={{ margin: '6px 0', borderColor: 'var(--border)' }} />
                <MenuItem className={classes.dangerItem} onClick={this.emitMenuRemoveUser}>
                    <ListItemIcon>
                        <DeleteOutlineIcon />
                    </ListItemIcon>
                    Remover do grupo
                </MenuItem>
            </Menu>
        )
    }
}

export const GrupoFormView = classToFComponent(GroupManagerFormViewClass, React)
export default GrupoFormView

function userRowId(row: UserGroupRow) {
    return row.id + ''
}

function userRowHeight() {
    return 58
}

function pluralize(count: number, singular: string, plural: string): string {
    return `${formatNumber(count)} ${count === 1 ? singular : plural}`
}

function statusOf(group?: GroupRow): { label: string; tone: LifesHubChipTone } {
    return group?.active ? { label: 'Ativo', tone: 'green' } : { label: 'Inativo', tone: 'gray' }
}

function countActive(options: FilterOption[], active?: Record<string, boolean>): number {
    return options.filter((option) => Boolean(active?.[option.key])).length
}

function buildUserColumns(
    openRowMenu: (row: UserGroupRow) => (event: React.MouseEvent<HTMLElement>) => void,
    groupId?: number,
    adminId?: number
): GridColDef[] {
    return [
        {
            field: 'full_name',
            headerName: 'Usuário',
            flex: 1.5,
            minWidth: 240,
            renderCell({ row }: GridRenderCellParams<UserGroupRow>) {
                return (
                    <CellIdentity>
                        <CellTitle title={row.full_name || row.name}>{row.full_name || row.name}</CellTitle>
                        <CellDesc title={row.name}>{row.name}</CellDesc>
                    </CellIdentity>
                )
            }
        },
        {
            field: 'preferred_group_id',
            headerName: 'Vínculo',
            width: 140,
            renderCell({ value }: GridRenderCellParams<UserGroupRow, number | undefined>) {
                const primary = Number(value) === Number(groupId)

                return (
                    <Chip
                        size="small"
                        label={primary ? 'Prioritário' : 'Secundário'}
                        sx={chipSx(primary ? 'blue' : 'gray')}
                    />
                )
            }
        },
        {
            field: 'can_export_data',
            headerName: 'Exportação',
            width: 140,
            renderCell({ value }: GridRenderCellParams<UserGroupRow, boolean | undefined>) {
                return (
                    <Chip size="small" label={value ? 'Liberada' : 'Bloqueada'} sx={chipSx(value ? 'green' : 'gray')} />
                )
            }
        },
        {
            field: 'id',
            headerName: 'Papel',
            width: 150,
            renderCell({ value }: GridRenderCellParams<UserGroupRow, number>) {
                return Number(value) === Number(adminId) ? (
                    <Chip size="small" label="Administrador" sx={chipSx('green')} />
                ) : (
                    <CellDesc>Membro</CellDesc>
                )
            }
        },
        {
            field: 'actions',
            headerName: '',
            width: 64,
            align: 'right',
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell({ row }: GridRenderCellParams<UserGroupRow>) {
                return (
                    <IconActionButton aria-label="Mais ações" onClick={openRowMenu(row)}>
                        <MoreVertIcon sx={{ fontSize: 20 }} />
                    </IconActionButton>
                )
            }
        }
    ]
}
